import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import { AutocompleteFunction } from '../../../../core/common/entities/autocompleteFunction.entity';
import { ExtendedBaseProfileField } from '../../../../core/profiles/entities/extended-profile-field.entity';
import { PartialRecord } from '../../../../core/records/entities/partial-record.class';
import { FieldInheritance } from '../../../../core/records/entities/record-inheritance.class';
import { LabelIcon } from '../../../../form-controls/components/_core/form-label/label-icon.entity';
import { FieldIconHelper } from '../../../helpers/icons/field-icon.helper';

@Component({
    standalone: true,
    selector: 'sh-base-generic-field',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShBaseGenericInputFieldComponent implements OnDestroy, OnInit {
    public readonly destroy$: Subject<void> = new Subject<void>();

    public readonly control$: ReplaySubject<UntypedFormControl> = new ReplaySubject<UntypedFormControl>(1);
    public readonly profileField$: ReplaySubject<ExtendedBaseProfileField> =
        new ReplaySubject<ExtendedBaseProfileField>(1);
    public readonly errorMsg$: ReplaySubject<string> = new ReplaySubject<string>(1);
    public readonly records$: ReplaySubject<PartialRecord[]> = new ReplaySubject<PartialRecord[]>(1);
    public readonly nestedIndex$: ReplaySubject<number> = new ReplaySubject<number>(1);
    public nestedPadding$: Observable<string>;
    public canBeIndeterminate = true;

    protected _fieldInheritance: FieldInheritance;
    public labelIcons: LabelIcon[] = [];

    protected readonly translateService: SharedLanguageService = inject(SharedLanguageService);
    protected readonly fieldIconHelper: FieldIconHelper = inject(FieldIconHelper);

    @Input() set profileField(extendedProfileField: ExtendedBaseProfileField) {
        this.profileField$.next(extendedProfileField);
    }

    @Input() set fieldInheritance(fieldInheritance: FieldInheritance) {
        if (fieldInheritance) {
            this._fieldInheritance = fieldInheritance;
            this.labelIcons = this.fieldIconHelper.setInheritanceIcon(this.labelIcons, fieldInheritance);
        }
    }

    @Input() set aiActive(aiActive: boolean) {
        if (aiActive) {
            this.labelIcons = this.fieldIconHelper.setAiIcon(this.labelIcons);
        }
    }

    @Input() set records(records: PartialRecord[]) {
        this.records$.next(records);
    }

    @Input() set control(control: UntypedFormControl) {
        this.control$.next(control);
    }

    @Input() set errorMsg(message: string) {
        this.errorMsg$.next(message);
    }

    @Input() set nestedIndex(nestedIndex: number) {
        this.nestedIndex$.next(nestedIndex);
    }

    @Input() autocompleteFct?: AutocompleteFunction;
    @Input() subFieldAutocompleteFcts: Record<string, AutocompleteFunction> = {};
    @Input() isBatchEdit = false;
    @Input() disabledMessageKey = '';
    @Input() clearable: boolean = false;

    ngOnInit(): void {
        this.setupInitListener();
        this.initNestedPadding();
    }

    private setupInitListener(): void {
        combineLatest([this.profileField$, this.records$, this.control$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([profileField, records, control]) => this.init(profileField, records, control));
    }

    protected initNestedPadding(): void {
        this.nestedPadding$ = this.nestedIndex$.pipe(
            map(index => {
                // Limit the max nesting to prevent UI glitches
                const nesting = Math.min(index, 2) + 1;
                return `padding-left: ${nesting}rem;`;
            })
        );
    }

    protected init(
        profileField: ExtendedBaseProfileField,
        records: PartialRecord[],
        control: UntypedFormControl
    ): void {}

    ngOnDestroy(): void {
        this.control$.complete();
        this.profileField$.complete();
        this.records$.complete();
        this.nestedIndex$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
